import type { IncidentListDTO } from '@eppendorf/vnls-notifications-utils';
import {
  Icon,
  IconSizeClasses,
  VirtualizedTable,
  Tooltip,
} from '@eppendorf/vnls-react-components';
import { createColumnHelper } from '@tanstack/react-table';
import { ReactElement, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useRouteError, useSearchParams } from 'react-router-dom';

import { CellTimestamp } from '$components/cell-timestamp/cell-timestamp';
import { DeviceNameCell } from '$components/device/name-cell';
import { LoadingErrorHint } from '$components/loading-error-hint/loading-error-hint';
import { useIncidentsWithQueryParams } from '$features/incidents/incidents.api';
import { IncidentListEmptyMessage } from '$features/incidents-list/incident-list-empty-message';
import { IncidentUnreadIndicator } from '$features/incidents-list/incident-unread-indicator';
import { IncidentsStatusBadge } from '$features/incidents-list/incidents-status';
import { getEventIconName } from '$features/monitoring/events/events.utils';
import { isValueWithKeyInSearchParams } from '$features/navigation/isValueWithKeyInSearchParams';
import { AddNotificationRuleDialog } from '$features/notifications/add-or-edit-notification-rule-dialog';

import './incidents-list.scss';

export function IncidentsList(): ReactElement {
  const { t } = useTranslation();
  const routeError = useRouteError();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleRowClick = (incident: IncidentListDTO) => {
    navigate({
      search: `?sidecarRoute=incident/flow&id=${incident.id}`,
    });
  };

  const [isNotificationDialogOpen, setIsNotificationDialogOpen] = useState(false);

  const { data, isError, isLoading, isFetching, fetchNextPage, hasNextPage } =
    useIncidentsWithQueryParams();
  const incidents = data?.pages.flat() ?? [];
  const columnHelper = createColumnHelper<IncidentListDTO>();
  const columns = useMemo(
    () => [
      columnHelper.accessor((info) => info, {
        id: 'incidentStatus',
        header: () => t('shared.status'),
        meta: { dataLabel: t('shared.status') },
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => (
          <div className="incidents-list__cell-status">
            <IncidentUnreadIndicator isRead={info.getValue().isRead} />
            <IncidentsStatusBadge incidentStatus={info.getValue().incidentStatus} />
          </div>
        ),
      }),
      columnHelper.accessor('createdAt', {
        header: () => t('incidentsTable.createdAt'),
        meta: { dataLabel: t('incidentsTable.createdAt') },
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => <CellTimestamp timestamp={info.renderValue()} isResponsive />,
      }),
      columnHelper.accessor('eventMessages.level', {
        header: () => t('incidentsTable.type'),
        meta: { dataLabel: t('incidentsTable.type') },
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: ({
          row: {
            original: { eventMessages: notificationsMessageInfo },
          },
        }) => {
          const notification = notificationsMessageInfo[0];

          return (
            <Icon
              name={`event-${getEventIconName(notification?.level)}`}
              size={IconSizeClasses.XSmall}
            />
          );
        },
      }),
      columnHelper.accessor('eventMessages.message', {
        header: () => t('incidentsTable.event'),
        meta: { dataLabel: t('incidentsTable.event') },
        cell: (info) => info.row.original.eventMessages[0]?.message,
      }),
      columnHelper.accessor('device.deviceName', {
        header: () => t('incidentsTable.device'),
        meta: { dataLabel: t('incidentsTable.device') },
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: ({
          row: {
            original: { device },
          },
        }) => <DeviceNameCell device={device} isIconUnmasked />,
      }),
      columnHelper.accessor('notificationRule', {
        header: () => t('incidentsTable.triggeredBy'),
        meta: { dataLabel: t('incidentsTable.triggeredBy') },
        cell: (info) => info.row.original.notificationRule.name,
      }),
    ],
    [],
  );

  return (
    <>
      {isNotificationDialogOpen && (
        <AddNotificationRuleDialog
          isOpen={isNotificationDialogOpen}
          onOpenChange={(open) => setIsNotificationDialogOpen(open)}
        />
      )}

      <div className="p-bottom-m p-top-m flex flex__ai--center gap-xs">
        {t('incidents.hint')}
        <Tooltip
          trigger={
            <div>
              <Icon name="info" size={IconSizeClasses.XSmall} />
            </div>
          }
        >
          <Trans
            i18nKey="incidents.note"
            components={{
              bold: <span className="font-weight-bold" />,
              regular: <span style={{ opacity: 0.8 }} />,
            }}
          />
        </Tooltip>
      </div>
      {(routeError || isError) && <LoadingErrorHint />}
      {!isLoading && !isError && (
        <div className="flex flex__dir--column" style={{ height: '100vh' }}>
          {incidents && (
            <VirtualizedTable
              data={incidents || []}
              columns={columns}
              loadMore={() => {
                if (hasNextPage && !isFetching) {
                  fetchNextPage();
                }
              }}
              onRowClick={handleRowClick}
              isSelectedRow={(incident: IncidentListDTO) =>
                isValueWithKeyInSearchParams('id', incident.id, searchParams)
              }
              isLoading={isFetching}
              isTableToCardDesign
              className="table--incidents-list"
            />
          )}

          {!isLoading && !isError && (!incidents || incidents?.length === 0) && (
            <IncidentListEmptyMessage
              openNotificationDialog={setIsNotificationDialogOpen}
            />
          )}
        </div>
      )}
    </>
  );
}
